import { AiReceptionistTriggers } from '../../../domain/models/AiReceptionist';

export function buildDefaultGreeting(
  template: string,
  { assistantName, represents, transmitsTo }: { assistantName: string; represents: string; transmitsTo: string },
): string {
  return template
    .replaceAll('{{assistant_name}}', assistantName || 'Donna')
    .replaceAll('{{public_label}}', represents || '')
    .replaceAll('{{callback_label}}', transmitsTo || '');
}

// The greeting is the catalog one: the raw template, or a copy rendered with the current labels
// or with labels still empty (frozen when Donna was turned on before they were filled in).
// Such a greeting is saved as the template, the backend fills the labels on every call.
export function isDefaultGreeting(
  greeting: string,
  template: string,
  { assistantName, represents, transmitsTo }: { assistantName: string; represents: string; transmitsTo: string },
): boolean {
  if (!template.trim()) return false;
  return [
    template,
    buildDefaultGreeting(template, { assistantName, represents, transmitsTo }),
    buildDefaultGreeting(template, { assistantName, represents: '', transmitsTo: '' }),
    buildDefaultGreeting(template, { assistantName, represents, transmitsTo: '' }),
    buildDefaultGreeting(template, { assistantName, represents: '', transmitsTo }),
  ].some((candidate) => candidate.trim() === greeting.trim());
}

const ASSISTANT_PATTERN = /assistante?/i;
const IA_PATTERN = /\bi\.?a\.?\b/i;
const FULL_DISCLOSURE_PATTERN = /intelligence artificielle/i;

export function greetingMentionsAiDisclosure(greeting: string): boolean {
  if (FULL_DISCLOSURE_PATTERN.test(greeting)) return true;
  return ASSISTANT_PATTERN.test(greeting) && IA_PATTERN.test(greeting);
}

// Summaries shown on the closed settings cards, empty values left out
export function buildPresentationChips(name: string, represents: string, transmitsTo: string): string[] {
  return [name, represents.trim(), transmitsTo.trim() && `Transmet à ${transmitsTo.trim()}`].filter(Boolean);
}

export function buildActivityChips(selectedActivitiesLabel: string, motifsCount: number, customMotifsCount: number): string[] {
  return [
    selectedActivitiesLabel,
    `${motifsCount} motif${motifsCount > 1 ? 's' : ''}`,
    customMotifsCount > 0 ? `${customMotifsCount} personnalisé${customMotifsCount > 1 ? 's' : ''}` : '',
  ].filter(Boolean);
}

export function buildInstructionsChips(instructions: string): string[] {
  return instructions.trim() ? ['1 consigne', `« ${instructions.trim()} »`] : ['Aucune consigne'];
}

export function buildPickupChips(triggers: AiReceptionistTriggers, ringCount: number): string[] {
  const values = Object.values(triggers);
  const checkedCount = values.filter(Boolean).length;
  return [
    checkedCount === values.length ? 'Toutes les situations' : `${checkedCount} situation${checkedCount > 1 ? 's' : ''}`,
    triggers.no_answer ? `Après ${ringCount} sonnerie${ringCount > 1 ? 's' : ''}` : '',
  ].filter(Boolean);
}
